import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { LocationStrategy } from '@angular/common';
import PersonsModel from 'src/app/core/models/persons.model';
import { PersonsService } from 'src/app/core/services/persons.service';
import { ProviderVisitsStatsService } from 'src/app/core/services/provider-visits-stats.service';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { FarmSiteService } from 'src/app/core/services/farm-site.service';
import { ItemMasterService } from 'src/app/core/services/item-master.service';
import ItemMasterModel from 'src/app/core/models/item-master.model';
import { ProductPlacementsService } from 'src/app/core/services/product-placements.service';

/**
 * DashboardCopyComponent
 *
 * This component serves as the dashboard for Provider Technicians and Business Admins.
 * It provides a comprehensive overview of business performance, including visit statistics,
 * product placements, and onboarding metrics. The dashboard supports filtering by date ranges
 * and drilling down into specific data points.
 *
 * Key Responsibilities:
 * - Display key performance indicators (KPIs) for provider visits and product placements.
 * - Provide date range filtering (Today, Last Week, Last Month, Last Quarter, Custom).
 * - Manage lists of Provider Managers and Technicians for hierarchical data viewing.
 * - Display product catalogs categorized by type (Healthcare, Feed, Other).
 * - Handle drill-down popups for detailed lists (Dealers, Farmers, Consultants, Products Placed/Returned).
 * - Calculate and display ratios (e.g., Own vs Competitor surveys).
 *
 * User Roles:
 * - Business Admin: Views aggregated data for the entire business and can drill down by manager.
 * - Provider Technician: Views data specific to their assigned tasks and visits.
 *
 * Features:
 * - **Stats Dashboard**: Visual representation of visits, approvals, declines, and financial metrics.
 * - **Date Filtering**: Flexible date selection for historical data analysis.
 * - **Product Catalog**: Categorized view of available products.
 * - **Drill-down Reports**: Detailed lists for onboarding and product transactions.
 * - **Responsive Design**: Adapts layout for mobile and desktop views.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`) and loads business persons (Managers/Technicians).
 * 2. Sets default tab and date range (Today).
 * 3. Fetches dashboard statistics based on selected date range and user role.
 * 4. Fetches product catalog data.
 * 5. User interactions (date change, tab switch, popup open) trigger data refreshes.
 *
 * @component
 * @selector app-dashboard-copy
 * @example
 * <app-dashboard-copy></app-dashboard-copy>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-provider-dashboard',
  templateUrl: './provider-dashboard.component.html',
  styleUrls: ['./provider-dashboard.component.scss']
})
export class ProviderDashboardComponent implements OnInit, OnDestroy {
  /**
   * Application permissions object.
   * @type {any}
   */
  public appPermission: any;

  /**
   * Current active tab index (0 for Overview, 1 for Stats).
   * @type {number}
   * @default 0
   */
  public tabIndex: number = 0;

  /**
   * Flag to control visibility of the date range popup.
   * @type {boolean}
   * @default false
   */
  public isDateRangePopupOpen: boolean = false;

  /**
   * List of Provider Managers associated with the business.
   * @type {Array<PersonsModel>}
   */
  public $providerManagersList: Array<PersonsModel>;

  /**
   * List of Provider Technicians associated with the business.
   * @type {Array<PersonsModel>}
   */
  public $providerTechniciansList: Array<PersonsModel>;

  /**
   * Object containing aggregated dashboard statistics.
   * @type {any}
   */
  public providerVisitsStats: any;

  /**
   * Start date for data filtering (YYYY-MM-DD).
   * @type {string}
   * @default ''
   */
  public from_date: string = '';

  /**
   * End date for data filtering (YYYY-MM-DD).
   * @type {string}
   * @default ''
   */
  public to_date: string = '';

  /**
   * Selected date range type (e.g., 'today', 'last-week', 'custom').
   * @type {string}
   * @default ''
   */
  public dateRange: string = '';

  /**
   * Custom start date selected by user.
   * @type {string}
   * @default ''
   */
  public customFromDate: string = '';

  /**
   * Custom end date selected by user.
   * @type {string}
   * @default ''
   */
  public customToDate: string = '';

  /**
   * Flag indicating if stats data is currently loading.
   * @type {boolean}
   * @default false
   */
  public statsDataLoader: boolean = false;

  /**
   * Flag indicating if the view is mobile (width <= 570px).
   * @type {boolean}
   */
  public isMobile: boolean = window.innerWidth <= 570;

  /**
   * Source identifier for the list popup (e.g., 'Dealers', 'Farmers').
   * @type {string}
   * @default ''
   */
  public isListFrom: string = '';

  /**
   * List of healthcare products.
   * @type {Array<ItemMasterModel>}
   * @default []
   */
  public healthcareProducts: Array<ItemMasterModel> = [];

  /**
   * List of feed products.
   * @type {Array<ItemMasterModel>}
   * @default []
   */
  public feedProducts: Array<ItemMasterModel> = [];

  /**
   * List of other products.
   * @type {Array<ItemMasterModel>}
   * @default []
   */
  public otherProducts: Array<ItemMasterModel> = [];

  /**
   * List of products placed during the selected period.
   * @type {any[]}
   */
  public productsPlaced = [];

  /**
   * List of products returned during the selected period.
   * @type {any[]}
   */
  public productsReturned = [];

  /**
   * List of onboarded dealers.
   * @type {any[]}
   */
  public onBoardedDealers = [];

  /**
   * List of onboarded farmers.
   * @type {any[]}
   */
  public onBoardedFarmers = [];

  /**
   * List of onboarded SMEs/Consultants.
   * @type {any[]}
   */
  public onBoardedSMEs = [];

  /**
   * Flag indicating if the list data fetch was successful (or empty).
   * @type {boolean}
   * @default false
   */
  public isListSucess: boolean = false;

  /**
   * Flag indicating if list data is currently loading.
   * @type {boolean}
   * @default false
   */
  public listLoader: boolean = false;

  /**
   * Constructor for DashboardCopyComponent
   *
   * Initializes the component and injects required services.
   *
   * @constructor
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @service ActivatedRoute
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PersonsService} personsService - Service for managing person data.
   * @service PersonsService
   * @param {ProviderVisitsStatsService} providerVisitsStatsService - Service for fetching visit statistics.
   * @service ProviderVisitsStatsService
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @service FarmSiteService
   * @param {ItemMasterService} itemMasterService - Service for product/item master data.
   * @service ItemMasterService
   * @param {ProductPlacementsService} productsPlacementService - Service for product placement data.
   * @service ProductPlacementsService
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @service ToastrService
   */
  constructor(
    public route: ActivatedRoute,
    private previousRouteService: PreviousRouteService,
    private locationStrategy: LocationStrategy,
    private personsService: PersonsService,
    private providerVisitsStatsService: ProviderVisitsStatsService,
    private farmSiteService: FarmSiteService,
    private itemMasterService: ItemMasterService,
    private productsPlacementService: ProductPlacementsService,
    private toasterService: ToastrService
  ) {
  }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Initializes lists for managers and technicians.
   * 2. Retrieves route data permissions.
   * 3. Tracks navigation history.
   * 4. Fetches business persons to set up the dashboard context.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    this.$providerManagersList = new Array();
    this.$providerTechniciansList = new Array();
    this.appPermission = this.route.snapshot.data['rolepermission'];
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    await this.getBusinessPersons();
  }

  /**
   * Host listener for window resize events.
   * Updates the `isMobile` flag based on window width.
   *
   * @method onResize
   * @returns {void}
   */
  @HostListener('window:resize', [])
  onResize() {
    this.isMobile = window.innerWidth <= 570;
  }

  /**
   * Fetches business persons (Managers and Technicians) based on the logged-in user's role.
   *
   * - Business Admin: Fetches all managers (level 2) and technicians (level 3).
   * - Other Roles: Fetches technicians reporting to the logged-in user.
   *
   * @private
   * @async
   * @method getBusinessPersons
   * @returns {Promise<void>}
   */
  private async getBusinessPersons() {
    try {
      this.statsDataLoader = true;
      const persons = await this.personsService.getPersonsbyBusinessID(localStorage.businessID);
      if (localStorage.loginPersonType == "Business Admin") {
        this.$providerManagersList = (persons ?? []).filter(
          (person) => person.level == 2
        );
        this.$providerTechniciansList = (persons ?? []).filter(
          (person) => person.level == 3
        );
      } else {
        this.$providerTechniciansList = (persons ?? []).filter(
          (person) =>
            person.level == 3 && person.reportingTo.id == localStorage.loginPersonId
        );
      }

      if (localStorage.loginPersonType == "Business Admin") {
        this.setTabIndex(0);
      } else {
        this.setTabIndex(1);
      }
    } catch (error) {
      this.toasterService.error(error);
    }
  }

  /**
   * Sets the active tab index and triggers data refresh.
   *
   * - Resets date filters.
   * - If tab 0 (Overview), fetches stats directly.
   * - If other tabs, sets date to 'today' which triggers fetch.
   *
   * @method setTabIndex
   * @param {number} index - The index of the tab to activate.
   * @returns {void}
   */
  public setTabIndex(index: number) {
    this.tabIndex = index;
    this.from_date = '';
    this.to_date = '';
    if (this.tabIndex !== 0) {
      this.setDate('today');
    } else {
      this.fetchingStatsData();
    }
  }

  /**
   * Fetches dashboard statistics and product data based on current filters.
   *
   * - Cancels ongoing requests.
   * - Determines stats to include based on active tab.
   * - Fetches stats via `ProviderVisitsStatsService`.
   * - Fetches product lists via `ItemMasterService`.
   *
   * @private
   * @async
   * @method fetchingStatsData
   * @returns {Promise<void>}
   */
  private async fetchingStatsData() {
    try {
      this.statsDataLoader = true;
      this.providerVisitsStatsService.cancelOnGoingHttpRequests();
      const includeStats = this.tabIndex === 0
        ? 'healthcare_products,feed_products,other_products,sites,dealers,farmers,smes'
        : 'visits,visits_approved,visits_declined,products_placed,qty_placed,value_placed,products_returned,qty_returned,value_returned,paid,due,own_surveys,competitor_surveys'

      // Get from_date and to_date values (assuming they exist in a component property)
      const queryParams: { from_date?: string; to_date?: string } = {};

      // Only add from_date if it's defined and not empty
      if (this.from_date) queryParams.from_date = this.from_date;

      // Only add to_date if it's defined and not empty
      if (this.to_date) queryParams.to_date = this.to_date;

      if (localStorage.loginPersonType == "Business Admin") {
        const providerVisitStats: any =
          await this.providerVisitsStatsService.getDashboardStatsByBusinessId(
            includeStats,
            queryParams.from_date,
            queryParams.to_date
          );
        this.providerVisitsStats = providerVisitStats?._businessAggregate[0];
      } else {
        const providerVisitStats: any =
          await this.providerVisitsStatsService.getDashboardStatsByManagerId(
            includeStats,
            queryParams.from_date,
            queryParams.to_date
          );
        this.providerVisitsStats = providerVisitStats?._managerAggregates[0];
      }
      const itemMasterResponse = await this.itemMasterService.getAllItemMasterbyFarmer(localStorage.businessID);
      this.healthcareProducts = itemMasterResponse?.filter(item => item.item_type_id == 2 && item.system == false) || [];
      this.feedProducts = itemMasterResponse?.filter(item => item.item_type_id == 1 && item.system == false) || [];
      this.otherProducts = itemMasterResponse?.filter(item => item.item_type_id != 1 && item.item_type_id != 2 && item.system == false) || [];
      this.statsDataLoader = false;
    } catch (error) {
      this.toasterService.error(error.error.message);
      this.statsDataLoader = false;
    } finally {
      this.statsDataLoader = false;
    }
  }

  /**
   * Sets the date range filter and updates the dashboard data.
   *
   * @method setDate
   * @param {string} dateRange - The date range key (e.g., 'today', 'last-week').
   * @returns {void}
   * @side-effect Updates from_date, to_date, and triggers data fetch.
   */
  public setDate(dateRange: string) {
    this.dateRange = dateRange;
    this.customFromDate = '';
    this.customToDate = '';
    this.setFromToDates(dateRange);
  }

  /**
   * Handles date range changes from mobile view.
   * Opens custom date popup if 'custom' is selected.
   *
   * @method onMobileDateChange
   * @param {string} value - The selected date range value.
   * @returns {void}
   */
  public onMobileDateChange(value: string) {
    if (value === 'custom') {
      this.openDateRangePopup();
    } else {
      this.setDate(value);
    }
  }

  /**
   * Calculates start and end dates based on the selected range key.
   *
   * @private
   * @method setFromToDates
   * @param {string} dateRange - The date range key.
   * @returns {void}
   */
  private setFromToDates(dateRange: string) {
    const today = new Date();
    let fromDate = new Date();
    let toDate = new Date();

    switch (dateRange) {
      case 'today':
        fromDate = new Date(today);
        toDate = new Date(today);
        break;
      case 'last-week':
        // Calculate the previous week's start and end dates
        toDate = new Date(today);
        toDate.setDate(today.getDate() - today.getDay()); // Last Sunday (end of last week)

        fromDate = new Date(toDate);
        fromDate.setDate(toDate.getDate() - 6); // Previous Monday (start of last week)        
        break;
      case 'last-month':
        fromDate.setMonth(today.getMonth() - 1);
        fromDate.setDate(1); // Start of last month
        toDate = new Date(today.getFullYear(), today.getMonth(), 0); // End of last month
        break;
      case 'last-quarter':
        const currentMonth = today.getMonth(); // 0 = January, 11 = December

        // Calculate the last quarter excluding the current month
        const lastQuarterEndMonth = currentMonth - 1; // End of the last quarter (e.g., Feb if current is Mar)
        const lastQuarterStartMonth = lastQuarterEndMonth - 2; // Start of the last quarter

        // Calculate the year for start and end dates, adjusting if the start month is from the previous year
        const currentYear = today.getFullYear();
        const startYear = lastQuarterStartMonth < 0 ? currentYear - 1 : currentYear;
        const endYear = lastQuarterEndMonth < 0 ? currentYear - 1 : currentYear;

        // Set the start and end dates of the last quarter
        fromDate = new Date(startYear, (lastQuarterStartMonth + 12) % 12, 1);
        toDate = new Date(endYear, (lastQuarterEndMonth + 12) % 12 + 1, 0);
        break;
      default:
        this.from_date = '';
        this.to_date = '';
        return;
    }

    this.from_date = this.formatDate(fromDate);
    this.to_date = this.formatDate(toDate);
    this.fetchingStatsData();
  }

  /**
   * Helper function to format dates as 'YYYY-MM-DD'.
   *
   * @private
   * @method formatDate
   * @param {Date} date - The date object to format.
   * @returns {string} Formatted date string.
   */
  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Opens the custom date range popup.
   *
   * @method openDateRangePopup
   * @returns {void}
   */
  public openDateRangePopup() {
    this.isDateRangePopupOpen = true;
  }

  /**
   * Closes the custom date range popup.
   *
   * @method closeDateRangePopup
   * @returns {void}
   */
  public closeDateRangePopup() {
    this.isDateRangePopupOpen = false;
  }

  /**
   * Applies the selected custom date range.
   *
   * @method onDateRangeApply
   * @param {object} event - Object containing fromDate and toDate strings.
   * @returns {void}
   */
  public onDateRangeApply(event: { fromDate: string; toDate: string }) {
    this.from_date = this.customFromDate = moment(event.fromDate).format('YYYY-MM-DD');
    this.to_date = this.customToDate = moment(event.toDate).format('YYYY-MM-DD');
    this.dateRange = 'custom';
    this.fetchingStatsData();
  }

  /**
   * Calculates the Greatest Common Divisor (GCD) of two numbers.
   * Used for ratio calculation.
   *
   * @private
   * @method getGCD
   * @param {number} a - First number.
   * @param {number} b - Second number.
   * @returns {number} The GCD.
   */
  private getGCD(a: number, b: number): number {
    while (b !== 0) {
      let temp = b;
      b = a % b;
      a = temp;
    }
    return a;
  }

  /**
   * Calculates and formats the ratio between two numbers (e.g., "3:2").
   *
   * @method getRatio
   * @param {number} [own=0] - First value (e.g., Own surveys).
   * @param {number} [competitor=0] - Second value (e.g., Competitor surveys).
   * @returns {string} Formatted ratio string.
   */
  public getRatio(own: number = 0, competitor: number = 0): string {
    own = +own || 0; // Convert to number and handle NaN
    competitor = +competitor || 0;

    if (own === 0 && competitor === 0) {
      return "0:0"; // Edge case where both are zero
    }
    if (own === 0) {
      return `0:1`; // Avoid division by zero
    }
    if (competitor === 0) {
      return `1:0`; // Avoid division by zero
    }

    const gcd = this.getGCD(own, competitor);
    return `${own / gcd}:${competitor / gcd}`;
  }

  /**
   * Checks if the current user is a Business Admin.
   *
   * @method isBusinessAdmin
   * @returns {boolean} True if Business Admin, else false.
   */
  public isBusinessAdmin() {
    return localStorage.loginPersonType == "Business Admin" ? true : false;
  }

  /**
   * Closes the list popup overlay.
   *
   * @method closePopup
   * @returns {void}
   */
  public closePopup() {
    document.querySelector('.popup-overlay').classList.add('hidden');
    document.querySelector('.popup-container').classList.add('hidden');
  }

  /**
   * Opens a detailed list popup based on the selected category.
   * Fetches data for Dealers, Farmers, Consultants, or Products.
   *
   * @async
   * @method openListPopup
   * @param {string} listFrom - The category of list to open (e.g., 'Dealers', 'Products Placed').
   * @returns {Promise<void>}
   * @side-effect Updates list data properties and shows popup.
   */
  public async openListPopup(listFrom: string) {
    [this.onBoardedDealers, this.onBoardedFarmers, this.onBoardedSMEs, this.productsPlaced, this.productsReturned] = [[], [], [], [], []];
    document.querySelector('.popup-overlay').classList.remove('hidden');
    document.querySelector('.popup-container').classList.remove('hidden');
    this.isListSucess = false;
    this.isListFrom = listFrom;
    this.listLoader = true;
    try {
      switch (listFrom) {
        case 'Dealers':
          this.onBoardedDealers = await this.farmSiteService.getDealersFarmersConsultantsList(localStorage.businessID, 'Dealer', this.from_date, this.to_date);
          this.isListSucess = this.onBoardedDealers.length ? false : true;
          break;
        case 'Farmers':
          this.onBoardedFarmers = await this.farmSiteService.getDealersFarmersConsultantsList(localStorage.businessID, 'Farmer', this.from_date, this.to_date)
          this.isListSucess = this.onBoardedFarmers.length ? false : true;
          break;
        case 'Consultants':
          this.onBoardedSMEs = await this.farmSiteService.getDealersFarmersConsultantsList(localStorage.businessID, 'SME', this.from_date, this.to_date)
          this.isListSucess = this.onBoardedSMEs.length ? false : true;
          break;
        case 'Products Placed':
          this.productsPlaced = await this.productsPlacementService.getAllProductsPlaced(this.from_date, this.to_date);
          this.isListSucess = this.productsPlaced.length ? false : true;
          break;
        case 'Products Returned':
          if (localStorage.loginPersonType == "Business Admin") {
            this.productsReturned = await this.productsPlacementService.getAllProductReturnedByBusinessLevel(this.from_date, this.to_date);
          } else {
            this.productsReturned = await this.productsPlacementService.getAllProductReturnedByManagerLevel(
              this.$providerTechniciansList.map((obj) => obj.id).join(","),
              this.from_date,
              this.to_date
            );
          }
          this.isListSucess = this.productsReturned.length ? false : true;
          break;
        default:
          break;
      }
    } catch (error) {
      this.listLoader = false;
      this.isListSucess = true;
    } finally {
      this.listLoader = false;
    }
  }

  /**
   * Displays a warning toast when list data is not available.
   *
   * @method noListView
   * @returns {void}
   */
  public noListView() {
    this.toasterService.warning('List data not available', '', {
      timeOut: 2000
    });
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for all services used in the dashboard.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.personsService.cancelOnGoingHttpRequests();
    this.providerVisitsStatsService.cancelOnGoingHttpRequests();
    this.productsPlacementService.cancelOnGoingHttpRequests();
    this.itemMasterService.cancelOnGoingHttpRequests();
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.personsService.cancelOnGoingHttpRequests();
  }
}
